import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2, ShieldOff, EyeOff } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

const PRIVACY = [
  ["hide_phone", "Hide phone number"],
  ["hide_email", "Hide email address"],
  ["hide_income", "Hide income"],
  ["hide_employer", "Hide employer/company"],
  ["hide_exact_location", "Hide exact location"],
  ["hide_photos", "Hide all photos from non-connections"],
  ["disable_sharing", "Disable public profile sharing link"],
];

const FIELD_VIS = [
  ["income", "Income"], ["employer", "Employer / Company"], ["college", "Education / College"],
  ["family", "Family details"], ["dob", "Date of birth"], ["location", "Exact location"],
];

export default function Settings() {
  const { logout, profile } = useAuth();
  const nav = useNavigate();
  const [foundMatch, setFoundMatch] = useState(!!profile?.found_match);
  const [privacy, setPrivacy] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [ignores, setIgnores] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setPrivacy(data.privacy || {}));
    api.get("/notification-preferences").then(({ data }) => setPrefs(data.preferences));
    api.get("/blocks").then(({ data }) => setBlocks(data.blocks));
    api.get("/ignores").then(({ data }) => setIgnores(data.ignores));
  }, []);

  if (!privacy || !prefs) return <FullSpinner />;

  const savePrivacy = async () => { setBusy(true); try { await api.put("/privacy", { privacy }); toast.success("Privacy updated"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const savePrefs = async (np) => { setPrefs(np); await api.put("/notification-preferences", { preferences: np }); };
  const unblock = async (id) => { await api.delete(`/block/${id}`); setBlocks((b) => b.filter((x) => x.blocked !== id)); };
  const unignore = async (id) => { await api.delete(`/ignore/${id}`); setIgnores((b) => b.filter((x) => x.target !== id)); };
  const resetRecs = async () => { try { const { data } = await api.post("/feedback/reset"); toast.success(`Recommendation learning reset (${data.cleared} cleared)`); } catch (e) { toast.error(apiError(e)); } };
  const toggleFoundMatch = async () => {
    const next = !foundMatch;
    if (next && !window.confirm("Mark that you found your match? This pauses your profile and stops new match notifications.")) return;
    try { await api.post("/profile/found-match", { found: next }); setFoundMatch(next); toast.success(next ? "Congratulations! Your profile is paused." : "Welcome back — your profile is active again."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delAccount = async () => {
    if (!window.confirm("Delete your account permanently? This removes your profile, matches and messages and cannot be undone.")) return;
    try { await api.delete("/account"); toast.success("Account deleted"); logout(); nav("/"); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="settings-page">
      <h1 className="font-serif text-3xl font-bold text-[#58111A]">Settings</h1>

      <Card title="Privacy Controls">
        <div className="space-y-2">
          {PRIVACY.map(([k, l]) => (
            <label key={k} className="flex items-center justify-between py-1.5 cursor-pointer">
              <span className="text-sm text-[#261A1C]">{l}</span>
              <input type="checkbox" data-testid={`privacy-${k}`} checked={!!privacy[k]} onChange={(e) => setPrivacy({ ...privacy, [k]: e.target.checked })} className="w-4 h-4 accent-[#781826]" />
            </label>
          ))}
        </div>
        <button onClick={savePrivacy} disabled={busy} className="btn-primary text-sm mt-3">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Privacy"}</button>
      </Card>

      <Card title="Advanced Privacy">
        <label className="flex items-center justify-between py-1.5 cursor-pointer">
          <span className="text-sm text-[#261A1C]">Invisible browsing <span className="text-xs text-[#9E7B1B]">(view profiles without leaving a trace)</span></span>
          <input type="checkbox" data-testid="privacy-invisible_browsing" checked={!!privacy.invisible_browsing} onChange={(e) => setPrivacy({ ...privacy, invisible_browsing: e.target.checked })} className="w-4 h-4 accent-[#781826]" />
        </label>
        <label className="flex items-center justify-between py-1.5 cursor-pointer">
          <span className="text-sm text-[#261A1C]">Hide my online status</span>
          <input type="checkbox" data-testid="privacy-online_status_hidden" checked={!!privacy.online_status_hidden} onChange={(e) => setPrivacy({ ...privacy, online_status_hidden: e.target.checked })} className="w-4 h-4 accent-[#781826]" />
        </label>
        <div className="text-xs font-semibold text-[#9E7B1B] uppercase mt-3 mb-1">Field-level visibility</div>
        {FIELD_VIS.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between py-1.5">
            <span className="text-sm text-[#261A1C]">{l}</span>
            <select data-testid={`fieldvis-${k}`} value={(privacy.field_visibility || {})[k] || "everyone"}
              onChange={(e) => setPrivacy({ ...privacy, field_visibility: { ...(privacy.field_visibility || {}), [k]: e.target.value } })}
              className="text-sm border border-[#E8DFD1] rounded-lg px-2 py-1 bg-white">
              <option value="everyone">Everyone</option><option value="connections">Connections only</option><option value="nobody">Nobody</option>
            </select>
          </label>
        ))}
        <button onClick={savePrivacy} disabled={busy} className="btn-primary text-sm mt-3">Save Advanced Privacy</button>
      </Card>

      <Card title="Recommendations">
        <p className="text-sm text-[#605255] mb-3">We refine your recommendations based on the profiles you mark "Not Interested". You can reset this learning at any time.</p>
        <button onClick={resetRecs} data-testid="reset-recommendations" className="btn-outline text-sm">Reset recommendation learning</button>
      </Card>

      <Card title="Relationship Status">
        <div className="flex items-center justify-between">
          <div><div className="text-sm font-medium text-[#261A1C]">I found my match</div>
            <div className="text-xs text-[#605255]">Pauses your profile and stops new match notifications. You can turn this off anytime.</div></div>
          <button onClick={toggleFoundMatch} data-testid="found-match-toggle"
            className={`relative w-12 h-6 rounded-full shrink-0 transition ${foundMatch ? "bg-emerald-500" : "bg-[#E8DFD1]"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition ${foundMatch ? "left-6" : "left-0.5"}`} />
          </button>
        </div>
      </Card>

      <Card title="Notification Preferences">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold text-[#9E7B1B] uppercase mb-1">Email</div>
            {Object.keys(prefs.email || {}).map((k) => (
              <label key={k} className="flex items-center justify-between py-1 cursor-pointer">
                <span className="text-sm capitalize text-[#261A1C]">{k.replace("_", " ")}</span>
                <input type="checkbox" checked={!!prefs.email[k]} onChange={(e) => savePrefs({ ...prefs, email: { ...prefs.email, [k]: e.target.checked } })} className="w-4 h-4 accent-[#781826]" />
              </label>
            ))}
          </div>
          <div>
            <div className="text-xs font-semibold text-[#9E7B1B] uppercase mb-1">Match Digest</div>
            <select className="mj-input" value={prefs.digest} onChange={(e) => savePrefs({ ...prefs, digest: e.target.value })}>
              <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="off">Off</option>
            </select>
            <label className="flex items-center justify-between py-2 mt-2 cursor-pointer">
              <span className="text-sm text-[#261A1C]">Marketing & promotions</span>
              <input type="checkbox" data-testid="pref-marketing" checked={prefs.marketing !== false} onChange={(e) => savePrefs({ ...prefs, marketing: e.target.checked })} className="w-4 h-4 accent-[#781826]" />
            </label>
            <label className="flex items-center justify-between py-2 cursor-pointer">
              <span className="text-sm text-[#261A1C]">Weekly workspace digest <span className="text-xs text-[#9E7B1B] block">Sunday email: who needs a reply, meetings & stale profiles</span></span>
              <input type="checkbox" data-testid="pref-weekly-digest" checked={!!prefs.weekly_digest} onChange={(e) => savePrefs({ ...prefs, weekly_digest: e.target.checked })} className="w-4 h-4 accent-[#781826]" />
            </label>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-[#F1E9DA]">
          <label className="flex items-center justify-between py-1 cursor-pointer">
            <span className="text-sm font-medium text-[#261A1C]">Quiet hours <span className="text-xs text-[#9E7B1B]">(pause non-urgent emails)</span></span>
            <input type="checkbox" data-testid="pref-quiet-enabled" checked={!!prefs.quiet_hours?.enabled}
              onChange={(e) => savePrefs({ ...prefs, quiet_hours: { ...(prefs.quiet_hours || { start: 22, end: 7 }), enabled: e.target.checked } })} className="w-4 h-4 accent-[#781826]" />
          </label>
          {prefs.quiet_hours?.enabled && (
            <div className="flex items-center gap-2 mt-2 text-sm">
              <span className="text-[#605255]">From</span>
              <input type="number" min="0" max="23" data-testid="pref-quiet-start" value={prefs.quiet_hours?.start ?? 22}
                onChange={(e) => savePrefs({ ...prefs, quiet_hours: { ...prefs.quiet_hours, start: Number(e.target.value) } })} className="w-16 mj-input" />
              <span className="text-[#605255]">to</span>
              <input type="number" min="0" max="23" data-testid="pref-quiet-end" value={prefs.quiet_hours?.end ?? 7}
                onChange={(e) => savePrefs({ ...prefs, quiet_hours: { ...prefs.quiet_hours, end: Number(e.target.value) } })} className="w-16 mj-input" />
              <span className="text-xs text-[#9E7B1B]">(UTC hour)</span>
            </div>
          )}
        </div>
      </Card>

      <Card title={`Ignored Profiles (${ignores.length})`}>
        {ignores.length === 0 ? <p className="text-sm text-[#605255]">No ignored profiles.</p> : ignores.map((i) => (
          <div key={i.target} className="flex items-center justify-between py-2 border-b border-[#F1E9DA] last:border-0">
            <span className="text-sm text-[#261A1C]">{i.profile?.full_name} <span className="text-[#9E7B1B] font-mono text-xs">{i.profile?.profile_code}</span></span>
            <button onClick={() => unignore(i.target)} className="text-sm text-[#781826] flex items-center gap-1"><EyeOff className="w-3.5 h-3.5" /> Undo</button>
          </div>
        ))}
      </Card>

      <Card title={`Blocked Users (${blocks.length})`}>
        {blocks.length === 0 ? <p className="text-sm text-[#605255]">No blocked users.</p> : blocks.map((b) => (
          <div key={b.blocked} className="flex items-center justify-between py-2 border-b border-[#F1E9DA] last:border-0">
            <span className="text-sm text-[#261A1C]">{b.profile?.full_name} <span className="text-[#9E7B1B] font-mono text-xs">{b.profile?.profile_code}</span></span>
            <button onClick={() => unblock(b.blocked)} className="text-sm text-[#781826] flex items-center gap-1"><ShieldOff className="w-3.5 h-3.5" /> Unblock</button>
          </div>
        ))}
      </Card>

      <Card title="Account">
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => { logout(); nav("/"); }} className="btn-outline flex-1">Logout</button>
          <button onClick={delAccount} data-testid="delete-account" className="flex-1 text-sm font-semibold text-[#BE123C] border border-[#FECACA] rounded-xl py-2.5 hover:bg-red-50 flex items-center justify-center gap-2"><Trash2 className="w-4 h-4" /> Delete Account</button>
        </div>
      </Card>
    </div>
  );
}

function Card({ title, children }) {
  return <div className="mj-card p-5"><h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-3">{title}</h2>{children}</div>;
}
